import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { supabase } from '../lib/supabase.js';

/**
 * /auth/debug — temporary diagnostic page to see what's happening when
 * Google OAuth fails silently. Dumps URL, session, profile, localStorage
 * keys, navigator/standalone state, SW status. No styling beyond <pre>
 * — we want raw data, not pretty output.
 *
 * Remove after the bug is found. Tracked in PLAN.md Phase 9 follow-ups.
 */
export function AuthDebug() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    void (async () => {
      const out: Record<string, unknown> = {};
      out.url = window.location.href;
      out.search = window.location.search;
      out.hash = window.location.hash;
      out.referrer = document.referrer;
      out.online = navigator.onLine;
      const w = window as Window & { matchMedia?: typeof window.matchMedia; navigator: Navigator & { standalone?: boolean } };
      out.standalone = w.matchMedia?.('(display-mode: standalone)').matches ?? null;
      out.iosStandalone = w.navigator.standalone ?? null;
      out.userAgent = navigator.userAgent;

      try {
        const { data: sess, error } = await supabase.auth.getSession();
        out.session = sess.session ? { user_id: sess.session.user.id, expires_at: sess.session.expires_at } : null;
        out.session_error = error?.message ?? null;
      } catch (e) {
        out.session_error = String(e);
      }

      try {
        const { data: u, error } = await supabase.auth.getUser();
        out.user = u.user ? { id: u.user.id, email: u.user.email, provider: u.user.app_metadata?.provider } : null;
        out.user_error = error?.message ?? null;
      } catch (e) {
        out.user_error = String(e);
      }

      // localStorage keys (names only — never log values, they may include tokens)
      const lsKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) lsKeys.push(k);
      }
      out.localStorage_keys = lsKeys;

      // OAuth callback snapshot — captured by the early script in index.html.
      // Shows the URL Google/Supabase actually sent the user to, even after
      // Supabase strips params via history.replaceState. Values intentionally
      // exposed (not just keys) — we need to see them to diagnose.
      out.oauth_callback_url = localStorage.getItem('fluent.oauth_callback_url');
      out.oauth_callback_at = localStorage.getItem('fluent.oauth_callback_at');
      out.oauth_callback_pathname = localStorage.getItem('fluent.oauth_callback_pathname');
      out.oauth_callback_search = localStorage.getItem('fluent.oauth_callback_search');
      out.oauth_callback_hash = localStorage.getItem('fluent.oauth_callback_hash');

      // Service worker status
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        out.sw_active = reg?.active?.scriptURL ?? null;
        out.sw_waiting = reg?.waiting?.scriptURL ?? null;
        out.sw_installing = reg?.installing?.scriptURL ?? null;
        out.sw_scope = reg?.scope ?? null;
      } else {
        out.sw_active = '(serviceWorker API unavailable)';
      }

      setData(out);
    })();
  }, []);

  const text = data ? JSON.stringify(data, null, 2) : 'loading…';

  return (
    <main style={{ padding: 20, fontFamily: 'system-ui, sans-serif', background: '#fff8ee', minHeight: '100dvh' }}>
      <h1 style={{ margin: '0 0 12px', fontSize: 22 }}>Auth Debug</h1>
      <p style={{ margin: '0 0 16px', color: '#5a4f42', fontSize: 13 }}>
        Share this page&apos;s contents (or a screenshot) with the developer.
      </p>
      <pre
        style={{
          fontSize: 12,
          lineHeight: 1.4,
          background: '#fff',
          padding: 12,
          borderRadius: 12,
          border: '1px solid #e6dccd',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {text}
      </pre>
      <p style={{ marginTop: 16, fontSize: 12 }}>
        <Link to="/login" style={{ color: '#58cc02', fontWeight: 700 }}>← Back to login</Link>
      </p>
    </main>
  );
}
